import { groq } from 'next-sanity';

import type { Locale } from '@/types/shared';

const internalLinkProjection = `defined(item) => {
  item -> {
    ...,
    audienceRef->,
    _type == 'programmeB2C' => {'audienceRef': *[_type == 'programmesSettings'][0] {audienceRefB2C->}.audienceRefB2C },
    _type == 'programmeB2B' => {'audienceRef': *[_type == 'programmesSettings'][0] {audienceRefB2B->}.audienceRefB2B }
}}`;

const abTestDeepBlockReference = groq`
abTest->{
  ...,
  control {
    ...,
    blocks[] {
      _type == 'reference' => @->,
      _type != 'reference' => @,
    }
  },
  variations[] {
    ...,
    blocks[] {
      _type == 'reference' => @->,
      _type != 'reference' => @,
    }
  }
}
`;

const blockOptions = `//groq
  _type == 'masonryGrid' => {
    ...,
    featureCards[] {
      ...,
      responsiveImage {
        ...,
        images[] {
          ...,
          image {
            ...,
            asset->
          }
        }
      }
    }
  },
  _type == 'showcaseCarousel' => {
    ...,
    slides[] {
      ...,
      _type == "fullImage" => {
        ...,
        responsiveImage {
          ...,
          images[] {
            ...,
            image {
              ...,
              asset->
            }
          }
        }
      },
      _type == 'testimonial' => {
        ...,
        testimonial{
          ...,
          defined(ctaButton) => {
            ctaButton{
              ...,
              defined(audienceRef) => {
                audienceRef->
              }
            }
          }
        }
      }
    }
  },
  _type == 'programmeSpecifications' => {
    specifications[] {
      ...,
      "url": file.asset->url
    }
  },
  _type == 'policyList' => {
    ...,
    policyLinks[] {
      ...,
      policy->{
        slug
      }
    }
  },
  _type == 'programmeCarouselNew' => {
    ...,
    programmes[]->{
      ...,
      _type == 'programmeB2C' => {
        'audienceRef': *[_type == 'programmesSettings'][0] {audienceRefB2C->}.audienceRefB2C,
      },
      _type == 'programmeB2B' => {
        'audienceRef': *[_type == 'programmesSettings'][0] {audienceRefB2B->}.audienceRefB2B,
      }
    },
  },
`;

const audienceRefOptions = `//groq
    defined(audienceRef) => {audienceRef->},
    carouselItems[]{..., audienceRef->, text[]{..., markDefs[]{..., audienceRef->}}},
    testimonials[]{..., ctaButton{..., audienceRef->}},
    ctaCards[]{..., audienceRef->},
    articles[]{..., audienceRef->},
    spotlight{resourceCard{..., audienceRef->}},
    secondaryCards[]{..., audienceRef->},
    columns[]{..., audienceRef->},
    heroContent{...,
          cta{..., audienceRef->},
          body[]{..., markDefs[]{..., audienceRef->}}},
    _type == "multipointColumnExplainer" || _type == "usp" => coalesce({ctaUrl->{..., audienceRef->}}, ctaUrl),
    _type == "twoColumnHeadingText" => {
      ctaRight{..., audienceRef->},
      ctaLeft{..., audienceRef->},
      links[]{..., audienceRef->},
    },
    actions[]{..., audienceRef->}, //ctaCard
    CTA{..., audienceRef->}, // ctaBanner
    contactInformation[]{..., audienceRef->}, // contact
    thankYouCTAHrefInternal->{..., audienceRef->}, // hubspotFormBlock
    accordion[]{..., body[]{..., markDefs[]{..., audienceRef->}}}, // accordions (FAQ, WithImage)
    _type == "accordionWithImage" || _type == "longFormContent" => subheading[]{..., markDefs[]{..., audienceRef->}},
    description[]{..., markDefs[]{..., audienceRef->}}, // checklist, HBFormBlock
    text[]{..., markDefs[]{..., audienceRef->}}, // collageText, TwoColumnHeadingText
    bulletPoints[]{...,markDefs[]{..., audienceRef->}}, // collageText
    ctaCards[]{..., actions[]{..., audienceRef->}, body[]{..., markDefs[]{..., audienceRef->}}}, // dualCtaCards
    ctaCard{ctaCard{..., actions[]{..., audienceRef->}, body[]{..., markDefs[]{..., audienceRef->}}}}, // employersCta
    tabs[]{..., ctaCard{ctaCard{..., actions[]{..., audienceRef->}, body[]{..., markDefs[]{..., audienceRef->}}}}}, // featureExplainer
    captionHeadingDescription{..., description[]{..., markDefs[]{..., audienceRef->}}}, // featureShowOffGrid, and CaptionHeadingDescription in general
    thankYouMsgBody[]{..., markDefs[]{..., audienceRef->}}, // HBFormBlock
    textUnderForm[]{..., markDefs[]{..., audienceRef->}}, // HBFormBlock
    sections[]{...,
          subheading[]{..., markDefs[]{..., audienceRef->}},
          subsections[]{..., body[]{..., markDefs[]{..., audienceRef->}}}}, // longFormContent
    ctaButton{..., audienceRef->}, // MultiColumn
    columnContent[]{..., body[]{..., markDefs[]{..., audienceRef->}}}, // MultiColumn
    _type == "multiColumnLayout" => {
      columns[]{...,
          CTA{..., audienceRef->},
          textOverlay[]{..., markDefs[]{..., audienceRef->}},
          audienceRef->,
          body[]{..., markDefs[]{..., audienceRef->}}
        }
    },
    pressItems[]{..., audienceRef->}, // Press
    action{..., audienceRef->}, // ProductHighlight, ProgrammeCarousel, CardCarousel
    cards[]{..., body[]{..., markDefs[]{..., audienceRef->}}}, // StackedCards
    items[]{..., itemBody[]{...,markDefs[]{..., audienceRef->}}}, // Timeline
    triageSections[]{..., audienceRef->}, // Triage
    uspItems[]{..., text[]{..., markDefs[]{..., audienceRef->}}},
    ctaBody[]{..., markDefs[]{..., audienceRef->}}, // USP
    usps[]{..., description[]{..., markDefs[]{..., audienceRef->}}}, // USP With Media
    values[]{..., description[]{..., markDefs[]{..., audienceRef->}}}, // Values

    _type == 'creativeHero' => { ..., ctaList[] { ..., defined(audienceRef) => {audienceRef->} } },

    _type == 'sliderCarousel' => {
    ...,
    slides[]{
      ...,
      defined(primaryCTA) => { primaryCTA{ ..., defined(audienceRef) => {audienceRef->} } },
      defined(secondaryCTA) => { secondaryCTA{..., defined(audienceRef) => {audienceRef->} } }
    }
  },
  _type == 'creativeSplitHero' => {
    ...,
    defined(caption) => {
      caption[] {
        ...,
        markDefs[]{..., defined(audienceRef) => {audienceRef->}, ${internalLinkProjection} }
      }
    },
    defined(subheading) => {
      subheading[] {
        ...,
        markDefs[]{..., defined(audienceRef) => {audienceRef->}, ${internalLinkProjection} }
      }
    }
  },
`;

const blockProjections = groq`
  ${blockOptions}
  ${audienceRefOptions}

  _type == 'reference' => @-> {
    ...,
    _type == 'blockCollection' => {
      blocks[] {
        ...,
        ${blockOptions}
        ${audienceRefOptions}
      }
    }
  },
  _type == 'abTestContainer' => {
    ...,
    ${abTestDeepBlockReference}
  },
`;

const addQueryJoin = (joinWith: string, slug: string) => {
  switch (joinWith) {
    case 'author':
      return groq`&& author._ref in *[_type=="blogAuthor" && slug.current=="${slug}"]._id`;
    case 'category':
      return groq`&& category[0]._ref in *[_type=="blogCategory" && slug.current=="${slug}"]._id`;
    default:
      return '';
  }
};

export const siteSettingsQuery = ({ locale }: { locale: Locale }) => groq`
*[_type == "siteSettings" && (language == "${locale}" || language in ["${locale}", "en-GB"])] | order(language desc) [0] {
  ...,
  audiences[]->
}
`;

export const blogSettingsQuery = ({ locale }: { locale: Locale }) => groq`
*[_type == "blogSettings" && (language == "${locale}" || language in ["${locale}", "en-GB"]) && !(_id in path('drafts.**'))]{
  ...,
  featuredPost->
} | order(language desc)[0]
`;

export const caseStudiesSettingsQuery = ({
  locale,
}: {
  locale: Locale;
}) => groq`
*[_type == "caseStudiesSettings" && (language == "${locale}" || _lang in ["${locale}", "en-GB"]) && !(_id in path('drafts.**'))] | order(language desc)[0]
`;

export const navigationCollectionQuery = ({
  locale,
}: {
  locale: Locale;
}) => groq`
*[_type == "navigation_ex" &&
  language == ${locale ? `'${locale}'` : `'en-GB'`}]
| order(audience->{order}.order asc, language desc)
{..., audience->,
  navItems[]
    {...,
    audienceRef->,
    regions[] {
      ...,
      items[] {..., manualCardItem{..., audienceRef->,  description[]{..., markDefs[]{..., audienceRef->}}}, referencedCardItem{..., post->{..., author->, category[]->}}},
      columns[] {
        ...,
          items[] {
            ...,
            audienceRef->
                  }
                },
              },
      }
}
`;

export const pageQuery = ({
  locale,
  slug,
  audienceRef,
  b2cAudience,
}: {
  locale: Locale;
  slug: string;
  audienceRef?: string;
  b2cAudience?: string;
}) => `//groq
*[_type == "page" && slug.current == "${slug}" && language in ["${locale}", "en-GB"] ${audienceRef ? ` && audienceRef._ref == '${audienceRef}'` : ''}
${b2cAudience ? ` && b2cAudience == '${b2cAudience}'` : ''}] {
  ...,
  "canonicalPage": canonicalPage->{slug},
  audienceRef->,
  blocks[] {
    ...,
    ${blockProjections}
  }
} | order(language desc) [0]
`;

export const allProgrammesQuery = ({
  region,
  pageAudience = null,
  candidateTypeFilter = null,
}) => {
  const filterPredicate =
    candidateTypeFilter === null
      ? ''
      : `&& '${candidateTypeFilter}' in candidateType`;

  const audiencePredicate = pageAudience
    ? `&& (audienceRef._ref == '${pageAudience._id}' || !defined(audienceRef))`
    : '';
  return `
  *[_type == "programme"  && unlisted != true && region == "${region}" ${filterPredicate} ${audiencePredicate}] | order(category desc, name) {
  ..., audienceRef->}
  `;
};

export const programmeQuery = ({
  slug,
  region,
  audienceRef,
}: {
  slug: string;
  region: Locale;
  audienceRef?: string;
}) =>
  groq`
*[_type == "programme" && slug.current == "${slug}" && region == "${region}" ${audienceRef ? ` && audienceRef._ref == '${audienceRef}'` : ''}][0]{
  ...,
  "canonicalPage": canonicalPage->{slug},
  audienceRef->,
  ctaList[]{..., audienceRef->},
  blocks[] {
    ...,
    ${blockProjections}
  }
}
`;

export const allProgrammesNewQuery = ({ region }) => {
  return `//groq
  *[(_type == "programmeB2C" || _type == 'programmeB2B')  && unlisted != true && region == "${region}" ] | order(category desc, name) {
    _id,
    slug,
    region
  }
  `;
};

export const programmeNewQuery = ({
  slug,
  programmeType,
  b2cAudience,
}: {
  slug: string;
  programmeType: 'generic' | 'programmeB2B' | 'programmeB2C';
  b2cAudience: string;
}) => {
  const programmeTypeOption =
    programmeType === 'generic'
      ? `(_type == 'programmeB2B' || _type == 'programmeB2C')`
      : `_type == '${programmeType}'`;

  const b2cAudienceOption =
    programmeType === 'programmeB2C' && b2cAudience
      ? ` && b2cAudience == '${b2cAudience}'`
      : '';

  return `//groq
*[${programmeTypeOption} && slug.current == "${slug}" ${b2cAudienceOption}][0]{
  ...,
  audienceRef->,
  defined(overview) => {
    overview {
      ...,
      body[]{
        ...,
        markDefs[]{..., defined(audienceRef) => {audienceRef->}, ${internalLinkProjection} }
      }
    }
  },
  linkedProgramme->,
  relatedSection{
    ...,
    programmes[]->{
      ...,
      _type == 'programmeB2C' => {
        'audienceRef': *[_type == 'programmesSettings'][0] {audienceRefB2C->}.audienceRefB2C,
      },
      _type == 'programmeB2B' => {
        'audienceRef': *[_type == 'programmesSettings'][0] {audienceRefB2B->}.audienceRefB2B,
      }
    }
  },
  sidebar{
    ...,
    tabs[]{
      ...,
      content[]{
        ...,
        _type == 'freeText'  => {
          ...,
          richText[]{
            ...,
            markDefs[]{..., defined(audienceRef) => {audienceRef->}, ${internalLinkProjection} }
          }
        },
        _type == 'certificate' => {
          ...,
          body[]{
            ...,
            markDefs[]{..., defined(audienceRef) => {audienceRef->}, ${internalLinkProjection} }
          },
          footer[]{
            ...,
            markDefs[]{..., defined(audienceRef) => {audienceRef->}, ${internalLinkProjection} }
          }

        }
      }
    },
    footer{
      ...,
      description[]{
        ...,
        markDefs[]{..., defined(audienceRef) => {audienceRef->}, ${internalLinkProjection} }
      },
      CTA{
        ...,
        audienceRef->,
      }
    }
  },
  defined(nestedAccordion) => {
    nestedAccordion {
      ...,
      pullQuote {
        ..., defined(CTA) => { CTA { ..., audienceRef-> } }
      },
      outerAccordion[]{
        ...,
        accordion[]{
          ...,
          body[]{
            ...,
            markDefs[]{...,
              audienceRef->,
              ${internalLinkProjection}
            }
          },
          accordion[] {
            ...,
            body[] {
              ...,
              markDefs[] {
                ...,
                audienceRef->,
                ${internalLinkProjection}
              }
            }
          }
        }
      }
    }
  }
}
`;
};

export const programmesSettingsQuery = () => groq`
  *[_type == "programmesSettings"] {audienceRefB2B->, audienceRefB2C->}[0]
`;

export const allInfoPagesQuery = () => groq`
*[_type == "infoPage" && unlisted != true && !(_id in path("drafts.**"))] | order(publishDate desc)[0..50] {
  slug,
  language,
  _lang
}
`;

export const allSlugsFromPagesQuery = () => groq`
*[_type == "page" && !(_id in path("drafts.**")) ]{
  slug,
  language,
  _lang
}
`;

export const infoPageQuery = ({
  slug,
  audienceRef,
}: {
  slug: string | string[];
  audienceRef?: string;
}) => {
  const fullSlug = Array.isArray(slug) ? slug.join('/') : slug;

  return groq`
    *[_type == "infoPage" && slug.current == "${fullSlug}" ${audienceRef ? ` && audienceRef._ref == '${audienceRef}'` : ''}] {
      ...,
      audienceRef->,
      blocks[] {
        ...,
        ${blockProjections}
      }
    }[0]
  `;
};

export const allPoliciesQuery = () => groq`
*[_type == "policy" && !(_id in path("drafts.**"))]
`;
export const policyQuery = ({ slug }) => groq`
*[_type == "policy" && slug.current == "${slug}"][0]
`;

export const formQuery = ({
  slug,
  locale,
}: {
  slug: string;
  locale: Locale;
}) => groq`
*[_type == "hubspotForm" && slug.current == "${slug}" && (language == "${locale}" || language in ["${locale}", "en-GB"])] {..., abTest->} | order(language desc)[0]
`;

export const sitemapBlogPostsQuery = () => groq`
*[_type == "blogPost" && unlisted != true ] { slug, publishDate, _updatedAt, language} | order(publishDate desc)
`;

export const getAllCategories = () => groq`
*[_type == "blogCategory"]
`;

export const getAuthorBySlug = (slug) => groq`
*[_type == "blogAuthor" && slug.current == "${slug}"]
`;

export const getCategoryBySlug = (slug) => groq`
*[_type == "blogCategory" && slug.current == "${slug}"]
`;

export const getSearchResults = (keyword, locale) => groq`
*[_type == "blogPost" && unlisted != true && (language == "${locale}" || _lang in ["${locale}"]) && (excerpt match "${keyword}" || title match "${keyword}" || alternateText match "${keyword}" || "${keyword}" in tags[].label) || heading match "${keyword}" || postBody match "${keyword}"] {_id} | order(publishDate desc)
`;

export const eventSettingsQuery = () => groq`
*[_type == "eventSettings"] | order(language desc) [0]
`;

export const allEventsQuery = ({ location }: { location: string }) => groq`
*[_type == "event" && ("${location}" in locations || "Global" in locations || "Virtual" in locations) ] {
  ...,
  eventType->
} | order(startsAtDate asc)
`;

export const upcomingEventsQuery = () => groq`
*[_type == "event" && dateTime(endsAt) > dateTime(now())]
`;

export const upcomingLiveEventsQuery = () => groq`
*[_type == "event" && dateTime(endsAt) > dateTime(now()) && eventType == "live"]
`;

export const upcomingOnDemandEventsQuery = () => groq`
*[_type == "event" && dateTime(endsAt) > dateTime(now()) && eventType == "onDemand"]
`;

export const caseStudyQuery = ({ locale, slug }) =>
  `//groq
 *[ _type == "caseStudy" && slug.current == "${slug}" && region in ["${locale}"]][0] {
  ...,
  relatedSection{..., stories[]->{..., category[]->}},
  sidePanel {
    image,
    keyStats,
    programmes[] -> {
      region,
      audienceRef ->,
      name,
      slug
    },
  },
  defined(ctaSection) => {
    ctaSection {
      ...,
      defined(CTA) => {
        CTA {
          ...,
          defined(audienceRef) => { audienceRef-> }
        }
      }
    }
  }
}`;

export const latestPostsQuery = ({
  locale,
  quanity = 3,
}: {
  locale: Locale;
  quanity?: number;
}) => groq`
{ "postsPerCategory": *[_type == "blogCategory"]{
    _id, "blogPosts": *[_type == "blogPost" && unlisted != true && (language == "${locale}" || _lang in ["${locale}"]) && defined(publishDate) && references(^._id)]{
      slug,
      postBody[0],
      category[]->,
      author->,
      featuredImage,
      title,
      excerpt,
      publishDate,
      alternateText,
      isDecorativeImage,
      heading,
      subheading
    } | order(publishDate desc)[0..${quanity - 1}]
  },
"allAuthors": *[_type == "blogAuthor"],
"categories": *[ _type == "blogCategory"],
"latestPosts": *[_type == "blogPost" && unlisted != true && (language == "${locale}" || _lang in ["${locale}"]) && defined(publishDate)]{
  _type,
  slug,
  postBody[0],
  category[]->,
  author->,
  featuredImage,
  title,
  excerpt,
  publishDate,
  alternateText,
  isDecorativeImage,
  heading,
  subheading
} | order(publishDate desc)[0..${quanity - 1}]
  }
`;

export const allBlogPostsQuery = () =>
  groq`
*[_type == "blogPost" && unlisted != true && !(_id in path("drafts.**"))] | order(publishDate desc)[0..50] {
  slug,
  language
}
`;
// Detail of groq functions used - https://www.sanity.io/doc-functions
export const blogPostQuery = ({
  locale,
  slug,
}: {
  locale: Locale;
  slug: string;
}) => groq`
*[ _type == "blogPost"  && slug.current == "${slug}" && (language == "${locale}" || _lang in ["${locale}"]) && !(_id in path('drafts.**'))] {
  _id,
  isDecorativeImage,
  alternateText,
  publishDate,
  featuredImage,
  backgroundColour,
  SEO,
  excerpt,
  slug,
  heading,
  subheading,
  title,
  tags,
  author->,
  category[]->,
  "relatedPosts": relatedPostsColumn[]{
     columns[] {
        ...chosenPost->{
          _id,
          category[]->,
          author->,
          excerpt,
          backgroundColour,
          featuredImage,
          title,
          publishDate,
          slug,
          _type
        }
    }
  }.columns,
  "postBody": postBody[]{
    ...,
    markDefs[]{
      ...,
      item->{
        region, // Programme region for routing /programmes/ and /programs/
        _type,
        slug,
        audienceRef->
      }
    }
  },
  "tocData": {
    "count": count(postBody[style == 'h2']),
    "headingKeys": postBody[style == 'h2']._key,
    "headingStrings": coalesce(array::compact(string::split(pt::text(postBody[style == 'h2']), '\n\n')), []),
    "headingIds": coalesce(string::split(array::join(string::split(lower(pt::text(postBody[style == 'h2'])), ' '), '-'), '\n\n'), []),
  }
} | order(language desc) [0]`;

type CurrentPagePostsProps = {
  locale: Locale;
  rangeStart: number;
  rangeEnd: number;
  joinWith?: string;
  slug?: string;
  search?: string;
};

export const getCaseStudies = ({
  locale,
  rangeStart,
  rangeEnd,
  search,
}: CurrentPagePostsProps) => {
  const searchQuery = `&& (pt::text(postBody) match "${search}*" || title match "${search}*" || description match "${search}*" || client match "${search}*")`;
  return `{"posts": *[_type == 'caseStudy' && region in ["${locale}"] ${searchQuery}]
  | score(
        pt::text(postBody) match "${search}*",
        boost(title match "${search}*", 4),
        boost(description match "${search}*", 2),
        boost(client match "${search}*", 3),
      )
  | order(_score desc, publishDate desc)
      [${rangeStart}..${rangeEnd}],
  "total": count(*[_type == "caseStudy" && (region in ["${locale}"]) && defined(publishDate)  ${searchQuery}])}`;
};

export const getCurrentPagePosts = ({
  locale,
  rangeStart = 0,
  rangeEnd = 10,
  joinWith = null,
  slug,
  search = '',
}: CurrentPagePostsProps) => {
  const addedJoin = addQueryJoin(joinWith, slug);
  const searchQuery = `&& (pt::text(postBody) match "${search}*" || title match "${search}*" || excerpt match "${search}*")`;

  return groq`
  {
    "posts": *[_type == "blogPost" && unlisted != true && (language == "${locale}" || _lang in ["${locale}"]) ${searchQuery} ${addedJoin}]
      | score(
        pt::text(postBody) match "${search}*",
        boost(title match "${search}*", 3),
        boost(excerpt match "${search}*", 2),
      )
      | order(_score desc, publishDate desc)
      [${rangeStart}..${rangeEnd}] {
        _type,
        _id,
        _score,
        slug,
        publishDate,
        postBody[0],
        category[]->,
        author->,
        featuredImage,
        language,
        tags[]->,
        title,
        excerpt,
        alternateText,
        isDecorativeImage
    },
    "total": count(*[_type == "blogPost" && unlisted != true && (language == "${locale}" || _lang in ["${locale}"]) && defined(publishDate) ${searchQuery} ${addedJoin}])
  }
  `;
};

export const localeTranslationsQuery = (page) => {
  const sharedId = page?._id?.replace(/__i18n_en-US/g, '');
  return groq`
  select(count(*[@._id match "${sharedId}*" && !(@._id match "draft*") && slug.current != null]) > 1 => *[@._id match "${sharedId}*" && !(@._id match "draft*") && slug.current != null]{ _type, slug, language }, null)
`;
};
